import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { cn } from '../lib/cn.js';
// Storage access is try/catch'd because Safari private mode + storage-
// blocked environments throw on `getItem`/`setItem` even though
// `window.sessionStorage` is truthy — same best-effort pattern as
// `apps/web/src/lib/active-org.ts` + `walkthrough.tsx`.
function sessionMarked(key) {
    if (typeof window === 'undefined' || !window.sessionStorage)
        return false;
    try {
        return window.sessionStorage.getItem(key) === '1';
    }
    catch {
        return false;
    }
}
function markSession(key) {
    if (typeof window === 'undefined' || !window.sessionStorage)
        return;
    try {
        window.sessionStorage.setItem(key, '1');
    }
    catch {
        // Quota / disabled / private — the one-per-session lock is
        // best-effort. Worst case the follow-back shows twice in a tab.
    }
}
export function OutcomeFollowback({ eyebrow, prediction, onAnswer, sessionKey = 'outcome-fb-shown', confirmedLabel = 'Confirmed', partialLabel = 'Partial', refutedLabel = 'Refuted', className, }) {
    const [visible, setVisible] = useState(() => !sessionMarked(sessionKey));
    // Mark the session slot as used on first render so a second mount in
    // the same tab silently renders nothing.
    useEffect(() => {
        if (visible)
            markSession(sessionKey);
    }, [visible, sessionKey]);
    function handleAnswer(s) {
        setVisible(false);
        onAnswer(s);
    }
    if (!visible)
        return null;
    return (_jsxs("div", { role: "group", "aria-label": "Prediction outcome follow-back", className: cn('flex flex-col gap-3 rounded-3 border border-dashed border-[var(--color-line-2)]', 'bg-[var(--color-surface)] p-4 text-[var(--color-ink)]', className), children: [_jsx("div", { className: "font-mono text-2xs uppercase tracking-[0.16em] text-[var(--color-ink-muted)]", children: eyebrow }), _jsx("p", { className: "m-0 text-xl leading-snug [font-family:var(--font-serif)] [text-wrap:balance]", children: prediction }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(OutcomeButton, { onClick: () => handleAnswer('confirmed'), tone: "sage", children: confirmedLabel }), _jsx(OutcomeButton, { onClick: () => handleAnswer('partial'), tone: "amber", children: partialLabel }), _jsx(OutcomeButton, { onClick: () => handleAnswer('refuted'), tone: "rose", children: refutedLabel })] })] }));
}
function OutcomeButton({ children, onClick, tone, }) {
    const toneInk = tone === 'sage'
        ? 'text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)]'
        : tone === 'amber'
            ? 'text-[var(--color-amber)] hover:bg-[var(--color-amber-soft)]'
            : 'text-[var(--color-rose)] hover:bg-[var(--color-rose-soft)]';
    return (_jsx("button", { type: "button", onClick: onClick, className: cn('j-press inline-flex items-center rounded-pill border border-[var(--color-line)] bg-[var(--color-surface-2)] px-3 py-1.5 text-sm transition-colors', toneInk, 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]'), children: children }));
}
